import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import type { JobWithRelations } from '@/types';
import { isDeclinedByTech, jobBoardStatus } from '@/utils/techProgress';

/**
 * Compact jobs dashboard, mirrors the Leads page pattern:
 *  - one pipeline strip (where jobs stand, respects the page filters)
 *  - three attention cards (what needs doing, ignores the date filter)
 */

export type JobsDashboardTab = 'ongoing' | 'scheduled' | 'on_hold' | 'quote_sent' | 'completed' | 'cancelled';

/** Which attention card was clicked; the page turns it into filters that reproduce the count. */
export type JobsAttentionKind = 'unassigned' | 'overdue' | 'awaiting_payment' | 'tech_declined';

interface JobsDashboardProps {
  /** Jobs after the page's date / tech / location filters — drives the pipeline strip. */
  jobs: JobWithRelations[];
  /** Every job for the company — drives the attention cards, which are to-do lists not period stats. */
  allJobs?: JobWithRelations[];
  onAttentionClick?: (kind: JobsAttentionKind) => void;
}

interface PipelineCellProps {
  label: string;
  value: string | number;
  colorClass: string; // bg-* token class for the 8px square
  sub?: React.ReactNode;
  tooltip?: string;
}

function PipelineCell({ label, value, colorClass, sub, tooltip }: PipelineCellProps) {
  return (
    <div className="px-4 py-3.5" title={tooltip}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <span className={cn('h-2 w-2 shrink-0 rounded-[2px]', colorClass)} aria-hidden="true" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tracking-tight tabular-nums">{value}</div>
      <div className="mt-0.5 min-h-[15px] text-[11px] leading-[15px] text-muted-foreground">{sub}</div>
    </div>
  );
}

interface AttentionCardProps {
  label: string;
  value: number;
  borderClass: string; // border-l-* token class
  valueClass: string; // text-* token class
  onClick?: () => void;
}

function AttentionCard({ label, value, borderClass, valueClass, onClick }: AttentionCardProps) {
  return (
    <Card
      className={cn('cursor-pointer border-l-[3px] transition-colors hover:bg-muted/50', borderClass)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <CardContent className="p-4">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className={cn('mt-1 text-2xl font-semibold tracking-tight tabular-nums', valueClass)}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

interface Counts {
  scheduled: number;
  ongoing: number;
  onHold: number;
  quoteSent: number;
  completed: number;
  cancelled: number;
  awaitingPayment: number;
}

function tally(jobs: JobWithRelations[]): Counts {
  const c: Counts = { scheduled: 0, ongoing: 0, onHold: 0, quoteSent: 0, completed: 0, cancelled: 0, awaitingPayment: 0 };
  for (const job of jobs) {
    switch (jobBoardStatus(job)) {
      case 'scheduled': c.scheduled += 1; break;
      case 'in_progress': c.ongoing += 1; break;
      case 'on_hold': c.onHold += 1; break;
      case 'quote_sent': c.quoteSent += 1; break;
      case 'completed':
        c.completed += 1;
        if (job.payment_status !== 'paid') c.awaitingPayment += 1;
        break;
      case 'cancelled': c.cancelled += 1; break;
    }
  }
  return c;
}

const LIVE_STATUSES = new Set(['scheduled', 'in_progress', 'on_hold', 'quote_sent']);

export function JobsDashboard({ jobs, allJobs, onAttentionClick }: JobsDashboardProps) {
  const { now, startOfDay } = useTimezoneUtils();

  const counts = useMemo(() => tally(jobs), [jobs]);

  const attention = useMemo(() => {
    const source = allJobs ?? jobs;
    const today = startOfDay(now());
    let unassigned = 0;
    let overdue = 0;
    let awaitingPayment = 0;
    let techDeclined = 0;
    for (const job of source) {
      if (LIVE_STATUSES.has(job.status) && !job.assigned_tech_id) unassigned += 1;
      // Declined jobs keep assigned_tech_id, so the unassigned count above never sees them.
      if (isDeclinedByTech(job)) techDeclined += 1;
      if (jobBoardStatus(job) === 'scheduled' && job.scheduled_date) {
        const d = job.scheduled_date.includes('T') ? new Date(job.scheduled_date) : new Date(job.scheduled_date + 'T00:00:00');
        if (d < today) overdue += 1;
      }
      if (job.status === 'completed' && job.payment_status !== 'paid') awaitingPayment += 1;
    }
    return { unassigned, overdue, awaitingPayment, techDeclined };
  }, [allJobs, jobs, now, startOfDay]);

  const decided = counts.completed + counts.cancelled;
  const closePct = decided > 0 ? (counts.completed / decided) * 100 : null;
  const open = counts.scheduled + counts.ongoing;

  return (
    <div className="space-y-3">
      {/* Pipeline strip — where jobs stand in the selected period */}
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 lg:divide-x lg:divide-border">
          <PipelineCell
            label="Open"
            value={open}
            colorClass="bg-info"
            sub={`${counts.scheduled} scheduled · ${counts.ongoing} ongoing`}
          />
          <PipelineCell
            label="On hold"
            value={counts.onHold}
            colorClass="bg-warning"
          />
          <PipelineCell
            label="Quote sent"
            value={counts.quoteSent}
            colorClass="bg-primary"
          />
          <PipelineCell
            label="Completed"
            value={counts.completed}
            colorClass="bg-success"
            sub={counts.awaitingPayment > 0 ? `${counts.awaitingPayment} awaiting payment` : undefined}
          />
          <PipelineCell
            label="Cancelled"
            value={counts.cancelled}
            colorClass="bg-destructive"
            sub={decided > 0 ? `${Math.round((counts.cancelled / decided) * 100)}% of decided` : undefined}
          />
          <PipelineCell
            label="Close %"
            value={closePct === null ? '—' : `${closePct.toFixed(0)}%`}
            colorClass="bg-success"
            tooltip="Completed / (Completed + Cancelled). Open jobs are not counted until they are decided."
            sub={decided > 0 ? `${counts.completed} of ${decided} decided` : 'No decided jobs in range'}
          />
        </div>
      </Card>

      {/* Attention — what needs doing, regardless of the date filter */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <AttentionCard
          label="Unassigned"
          value={attention.unassigned}
          borderClass="border-l-destructive"
          valueClass="text-destructive"
          onClick={() => onAttentionClick?.('unassigned')}
        />
        <AttentionCard
          label="Declined by tech"
          value={attention.techDeclined}
          borderClass="border-l-destructive"
          valueClass="text-destructive"
          onClick={() => onAttentionClick?.('tech_declined')}
        />
        <AttentionCard
          label="Overdue (scheduled in the past)"
          value={attention.overdue}
          borderClass="border-l-warning"
          valueClass="text-warning"
          onClick={() => onAttentionClick?.('overdue')}
        />
        <AttentionCard
          label="Awaiting payment"
          value={attention.awaitingPayment}
          borderClass="border-l-info"
          valueClass="text-info"
          onClick={() => onAttentionClick?.('awaiting_payment')}
        />
      </div>
    </div>
  );
}
